import { useSyncExternalStore } from 'react';
import type { Pet3dModule } from './types';

// One shared import for the whole app (the useShatter lesson: never one import per effect).
let pending: Promise<Pet3dModule | null> | null = null;
let resolved: Pet3dModule | null | undefined;
let override: Pet3dModule | null | undefined;
const listeners = new Set<() => void>();

export function webglAvailable(): boolean {
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)) return false;
  try {
    const c = document.createElement('canvas');
    const ctx = c.getContext('webgl2') || c.getContext('webgl');
    // The probe canvas is thrown away, but a held WebGL context still counts against the
    // browser's real context limit until GC'd -- release it immediately.
    ctx?.getExtension('WEBGL_lose_context')?.loseContext();
    return !!ctx;
  } catch {
    return false;
  }
}

export function loadPet3d(): Promise<Pet3dModule | null> {
  if (override !== undefined) return Promise.resolve(override);
  pending ??= (webglAvailable()
    // @ts-expect-error -- untyped module (mirrors useShatter.ts's ../fx/shatter.js import)
    ? (import('./index.js') as Promise<Pet3dModule>).catch((e) => { console.warn('pet3d unavailable', e); return null; })
    : Promise.resolve(null)
  ).then((m) => { resolved = m; listeners.forEach((l) => l()); return m; });
  return pending;
}

/**
 * 3D failed for good this session (a lost context that would not come back, or a renderer that
 * could not be rebuilt). Clears any test override too: once unavailable, nothing should be able
 * to report otherwise for the rest of the session, in a test any more than in production (where
 * `override` is always `undefined` and this line is a no-op).
 */
export function markPet3dUnavailable(): void {
  resolved = null;
  override = null;
  pending = Promise.resolve(null);
  listeners.forEach((l) => l());
}

const currentModule = () => (override !== undefined ? override : resolved ?? null);

/**
 * useSyncExternalStore, not useState+useEffect: its subscription wrapper checks the new snapshot
 * against the last one (Object.is) BEFORE scheduling any React update, so a no-op notification
 * (e.g. jsdom's null -> null on every loadPet3d().then(on)) never dispatches a state update
 * outside act() in a test that never expects one. Plain useState's own eager-bailout still
 * renders once to discover the value is unchanged, which is enough to trigger the warning.
 */
function subscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  void loadPet3d().then(onStoreChange);
  return () => { listeners.delete(onStoreChange); };
}

export function usePet3d(): Pet3dModule | null {
  return useSyncExternalStore(subscribe, currentModule);
}

export type Pet3dStatus = 'loading' | 'ready' | 'unavailable';
const currentStatus = (): Pet3dStatus => {
  const m = override !== undefined ? override : resolved;
  return m === undefined ? 'loading' : m ? 'ready' : 'unavailable';
};

/** Whether 3D is still loading, up, or not coming this session (no WebGL, a failed import, a lost
 *  context) -- for screens that must SAY the 2D fallback is not the whole picture (the wardrobe). */
export function usePet3dStatus(): Pet3dStatus {
  return useSyncExternalStore(subscribe, currentStatus);
}

/** Tests only: undefined restores the real loader. */
export function setPet3dForTest(mod: Pet3dModule | null | undefined): void {
  override = mod;
  pending = null;
  resolved = undefined;
  listeners.forEach((l) => l());
}
